<template>
  <comDialog
    :isShow="dialogVisible"
    :width="700"
    title="配置显示列"
    alignCenter
    @close="handleClose"
  >
    <div style="padding: 0 20px;">
      <!-- 列选择 -->
      <div class="mb-3">
        <label class="form-label">选择要显示的列字段：</label>
        
        <!-- 自定义Transfer组件 -->
        <div class="custom-transfer">
          <!-- 左侧：可用列 -->
        <div class="transfer-panel">
          <div class="panel-header">
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <span>全部列 ({{ availableColumns.length }})</span>
              <el-checkbox 
                v-model="leftSelectAll" 
                @change="handleLeftSelectAll"
              >
                全选
              </el-checkbox>
            </div>
            <!-- <el-input
              v-model="leftSearchText"
              placeholder="输入列名搜索"
              size="small"
              clearable
              class="search-input"
              @input="watchLeftSearch"
            >
              <template #prefix>
                <el-icon class="el-input__icon"><Search /></el-icon>
              </template>
            </el-input> -->
          </div>
            <div class="panel-body">
              <div 
                v-for="column in filteredLeftColumns" 
                :key="column.field"
                class="transfer-item"
                :class="{ 'disabled': targetKeys.includes(column.field) }"
                @click="addColumn(column.field)"
              >
                <el-checkbox 
                  v-model="leftColumnChecks[column.field]" 
                  :disabled="targetKeys.includes(column.field)"
                >
                  {{ column.headerName }}
                </el-checkbox>
              </div>
              <div v-if="filteredLeftColumns.length === 0" class="empty-tip">
                无匹配列
              </div>
            </div>
          </div>
          
          <!-- 中间按钮 -->
          <div class="transfer-buttons">
            <el-button 
              type="primary" 
              icon="Plus" 
              size="small"
              @click="addSelectedLeftColumns"
              :disabled="selectedLeftColumns.length === 0"
            />
            <el-button 
              type="primary" 
              icon="Minus" 
              size="small"
              @click="removeSelectedRightColumns"
              :disabled="selectedRightColumns.length === 0"
            />
          </div>
          
          <!-- 右侧：已选列 -->
        <div class="transfer-panel">
          <div class="panel-header">
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <span>显示列 ({{ targetKeys.length }})</span>
              <el-checkbox 
                v-model="rightSelectAll" 
                @change="handleRightSelectAll"
              >
                全选
              </el-checkbox>
            </div>
            <!-- <el-input
              v-model="rightSearchText"
              placeholder="输入列名搜索"
              size="small"
              clearable
              class="search-input"
              @input="watchRightSearch"
            >
              <template #prefix>
                <el-icon class="el-input__icon"><Search /></el-icon>
              </template>
            </el-input> -->
          </div>
            <div class="panel-body right-panel-body" ref="rightPanel">
              <div 
                v-for="field in filteredTargetKeys" 
                :key="field"
                class="transfer-item"
                draggable="true"
                @dragstart="handleDragStart($event, field)"
                @dragover="handleDragOver($event)"
                @drop="handleDrop($event, field)"
                @dragend="handleDragEnd"
              >
                <div class="transfer-item-content">
                  <el-checkbox 
                    v-model="rightColumnChecks[field]"
                  >
                    {{ getColumnHeaderName(field) }}
                  </el-checkbox>
                  <div class="move-buttons">
                    <el-button
                      size="small"
                      type="text"
                      :icon="ArrowUp"
                      @click.stop="moveItemUp(field)"
                      title="上移"
                      :disabled="!canMoveUp(field)"
                    />
                    <el-button
                      size="small"
                      type="text"
                      :icon="ArrowDown"
                      @click.stop="moveItemDown(field)"
                      title="下移"
                      :disabled="!canMoveDown(field)"
                    />
                  </div>
                </div>
              </div>
              <div v-if="filteredTargetKeys.length === 0" class="empty-tip">
                无已选列
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button 
        type="primary" 
        @click="submitSelectedColumn"
      >确定</el-button>
    </div>
  </comDialog>
</template>

<script setup>
import { ref, defineExpose, defineEmits, nextTick, computed } from 'vue'
import comDialog from '@/components/comDialog/index.vue'
import { ArrowUp, ArrowDown } from '@element-plus/icons-vue'
import comCueBox from "@/components/comCue/comCue";

// 定义事件
const emit = defineEmits(['success', 'close'])

// 弹窗状态
const dialogVisible = ref(false)
const availableColumns = ref([])
const targetKeys = ref([])
const pageKey = ref('')

// 搜索文本
const leftSearchText = ref('')
const rightSearchText = ref('')

// 全选状态
const leftSelectAll = ref(false)
const rightSelectAll = ref(false)

// 右侧面板引用
const rightPanel = ref(null)

// 拖拽相关
let draggedField = null

// 复选框状态
const rightColumnChecks = ref({})
const leftColumnChecks = ref({})

// 计算属性：左侧过滤后的列
const filteredLeftColumns = computed(() => {
  if (!leftSearchText.value) {
    return availableColumns.value
  }
  const searchText = leftSearchText.value.toLowerCase()
  return availableColumns.value.filter(col => 
    col.headerName.toLowerCase().includes(searchText) && 
    !targetKeys.value.includes(col.field)
  )
})

// 计算属性：右侧过滤后的列
const filteredTargetKeys = computed(() => {
  if (!rightSearchText.value) {
    return targetKeys.value
  }
  const searchText = rightSearchText.value.toLowerCase()
  return targetKeys.value.filter(field => 
    getColumnHeaderName(field).toLowerCase().includes(searchText)
  )
})

// 计算属性：左侧选中的列
const selectedLeftColumns = computed(() => {
  return availableColumns.value
    .filter(col => !targetKeys.value.includes(col.field) && leftColumnChecks.value[col.field])
    .map(col => col.field)
})

// 计算属性：右侧选中的列
const selectedRightColumns = computed(() => {
  return targetKeys.value
    .filter(field => rightColumnChecks.value[field])
})

// 根据字段获取列名
const getColumnHeaderName = (field) => {
  const column = availableColumns.value.find(col => col.field === field)
  return column ? column.headerName : field
}

// 打开弹窗
const openDialog = (allColumns, currentPageKey) => {
  // 过滤出有效的列配置
  availableColumns.value = allColumns.filter(col => 
    col.field && col.headerName && typeof col.field === 'string'
  )
  
  pageKey.value = currentPageKey
  
  // 从缓存读取配置
  const cachedColumns = getCachedColumns()
  if (cachedColumns && cachedColumns.length > 0) {
    targetKeys.value = cachedColumns
  } else {
    // 默认显示所有列
    targetKeys.value = availableColumns.value.map(col => col.field)
  }
  
  // 初始化复选框状态
  initCheckboxStates()
  
  dialogVisible.value = true
  
  // 初始化排序功能
  nextTick(() => {
    initSorting()
  })
}

// 初始化复选框状态
const initCheckboxStates = () => {
  // 初始化左侧复选框状态
  leftColumnChecks.value = {}
  availableColumns.value.forEach(col => {
    leftColumnChecks.value[col.field] = false
  })
  
  // 初始化右侧复选框状态
  rightColumnChecks.value = {}
  targetKeys.value.forEach(field => {
    rightColumnChecks.value[field] = false
  })
  
  // 重置全选状态
  leftSelectAll.value = false
  rightSelectAll.value = false
}

// 初始化排序功能
const initSorting = () => {
  // 拖拽功能已通过Vue事件实现，这里不需要额外初始化
}

// 检查字段是否可以上移
const canMoveUp = (field) => {
  const index = targetKeys.value.indexOf(field)
  return index > 0
}

// 检查字段是否可以下移
const canMoveDown = (field) => {
  const index = targetKeys.value.indexOf(field)
  return index < targetKeys.value.length - 1
}

// 上移字段
const moveItemUp = (field) => {
  const index = targetKeys.value.indexOf(field);
  if (index > 0) {
    const newOrder = [...targetKeys.value];
    // 交换位置
    [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
    // 更新数据
    targetKeys.value = newOrder
  }
}

// 下移字段
const moveItemDown = (field) => {
  const index = targetKeys.value.indexOf(field);
  if (index < targetKeys.value.length - 1) {
    const newOrder = [...targetKeys.value];
    // 交换位置
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    // 更新数据
    targetKeys.value = newOrder
  }
}

// 拖拽开始
const handleDragStart = (event, field) => {
  draggedField = field
  event.dataTransfer.effectAllowed = 'move'
  event.dataTransfer.setData('text/plain', field)
  event.target.classList.add('dragging')
}

// 拖拽悬停
const handleDragOver = (event) => {
  event.preventDefault()
  event.dataTransfer.dropEffect = 'move'
}

// 拖拽结束
const handleDragEnd = (event) => {
  event.target.classList.remove('dragging')
  draggedField = null
}

// 放置
const handleDrop = (event, targetField) => {
  event.preventDefault()
  
  if (draggedField === targetField) return
  
  const draggedIndex = targetKeys.value.indexOf(draggedField)
  const targetIndex = targetKeys.value.indexOf(targetField)
  
  if (draggedIndex === -1 || targetIndex === -1) return
  
  // 创建新的顺序数组
  const newOrder = [...targetKeys.value]
  newOrder.splice(draggedIndex, 1)
  newOrder.splice(targetIndex, 0, draggedField)
  
  // 更新顺序
  targetKeys.value = newOrder
}

// 添加列到右侧
const addColumn = (field) => {
  if (!targetKeys.value.includes(field)) {
    targetKeys.value.push(field)
    // 重置复选框状态
    leftColumnChecks.value[field] = false
    // 检查全选状态是否需要更新
    checkLeftSelectAll()
  }
}

// 添加左侧选中的列
const addSelectedLeftColumns = () => {
  selectedLeftColumns.value.forEach(field => {
    if (!targetKeys.value.includes(field)) {
      targetKeys.value.push(field)
      leftColumnChecks.value[field] = false
    }
  })
  // 检查全选状态是否需要更新
  checkLeftSelectAll()
  checkRightSelectAll()
}

// 移除右侧选中的列
const removeSelectedRightColumns = () => {
  selectedRightColumns.value.forEach(field => {
    const index = targetKeys.value.indexOf(field)
    if (index > -1) {
      targetKeys.value.splice(index, 1)
      delete rightColumnChecks.value[field]
    }
  })
  // 检查全选状态是否需要更新
  checkLeftSelectAll()
  checkRightSelectAll()
}

// 检查左侧是否全选
const checkLeftSelectAll = () => {
  if (filteredLeftColumns.value.length === 0) {
    leftSelectAll.value = false
    return
  }
  
  leftSelectAll.value = filteredLeftColumns.value.every(col => 
    !targetKeys.value.includes(col.field) && leftColumnChecks.value[col.field]
  )
}

// 检查右侧是否全选
const checkRightSelectAll = () => {
  if (filteredTargetKeys.value.length === 0) {
    rightSelectAll.value = false
    return
  }
  
  rightSelectAll.value = filteredTargetKeys.value.every(field => 
    rightColumnChecks.value[field]
  )
}

// 左侧全选处理
const handleLeftSelectAll = () => {
  filteredLeftColumns.value.forEach(col => {
    if (!targetKeys.value.includes(col.field)) {
      leftColumnChecks.value[col.field] = leftSelectAll.value
    }
  })
}

// 右侧全选处理
const handleRightSelectAll = () => {
  filteredTargetKeys.value.forEach(field => {
    rightColumnChecks.value[field] = rightSelectAll.value
  })
}

// 监听左侧搜索文本变化，重置选中状态
const watchLeftSearch = () => {
  // 重置左侧选中状态
  availableColumns.value.forEach(col => {
    leftColumnChecks.value[col.field] = false
  })
  leftSelectAll.value = false
}

// 监听右侧搜索文本变化，重置选中状态
const watchRightSearch = () => {
  // 重置右侧选中状态
  targetKeys.value.forEach(field => {
    rightColumnChecks.value[field] = false
  })
  rightSelectAll.value = false
}

// 保存选择的列
const submitSelectedColumn = () => {
  let columnsToSave = [...targetKeys.value]
  
  // 如果已选列为空，则使用全部字段
  if (columnsToSave.length === 0) {
    comCueBox("显示列为空的情况默认会使用全部列字段?").then(() => {
        columnsToSave = availableColumns.value.map(col => col.field);
          // 保存到缓存
        saveColumnsToCache(columnsToSave)
        
        // 返回结果给父组件
        emit('success', columnsToSave)
        
        // 关闭弹窗
        handleClose()
    });
    return 
  }
  
  // 保存到缓存
  saveColumnsToCache(columnsToSave)
  
  // 返回结果给父组件
  emit('success', columnsToSave)
  
  // 关闭弹窗
  handleClose()
}

// 从缓存获取列配置
const getCachedColumns = () => {
  if (!pageKey.value) return null
  
  try {
    const cacheKey = 'column_config_map'
    const cachedMap = localStorage.getItem(cacheKey)
    if (cachedMap) {
      const columnConfigMap = JSON.parse(cachedMap)
      return columnConfigMap[pageKey.value] || null
    }
    return null
  } catch (error) {
    console.error('读取列配置缓存失败:', error)
    return null
  }
}

// 保存列配置到缓存
const saveColumnsToCache = (columns) => {
  if (!pageKey.value) return
  
  try {
    const cacheKey = 'column_config_map'
    const cachedMap = localStorage.getItem(cacheKey)
    const columnConfigMap = cachedMap ? JSON.parse(cachedMap) : {}
    
    // 更新当前页面的配置
    columnConfigMap[pageKey.value] = columns
    
    // 保存整个map
    localStorage.setItem(cacheKey, JSON.stringify(columnConfigMap))
  } catch (error) {
    console.error('保存列配置缓存失败:', error)
  }
}

// 关闭弹窗
const handleClose = () => {
  dialogVisible.value = false
  emit('close')
}

// 暴露方法给父组件
defineExpose({
  openDialog
})
</script>

<style scoped>
/* 自定义Transfer容器 */
.custom-transfer {
  display: flex;
  gap: 20px;
  margin-top: 10px;
  align-items: stretch;
  height: 400px;
}

/* 面板样式 */
.transfer-panel {
  flex: 1;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  background-color: #fff;
}

/* 面板头部 */
.panel-header {
  padding: 10px;
  border-bottom: 1px solid #dcdfe6;
  background-color: #f5f7fa;
  border-radius: 4px 4px 0 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.panel-header span {
  font-weight: 500;
  color: #303133;
}

/* 搜索输入框 */
.search-input {
  width: 100%;
}

/* 面板内容 */
.panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.right-panel-body {
  padding: 8px 0;
}

/* 项目样式 */
.transfer-item {
  cursor: pointer;
  border-radius: 4px;
  margin-bottom: 4px;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  padding: 0 10px;
}

.transfer-item:hover:not(.disabled) {
  background-color: #f5f7fa;
}

.transfer-item.disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

/* 右侧项目内容 */
.transfer-item-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

/* 移动按钮 */
.move-buttons {
  display: flex;
  gap: 4px;
  margin-left: 8px;
}

.move-buttons .el-button {
  padding: 0;
  font-size: 12px;
  color: #606266;
}

.move-buttons .el-button:hover {
  color: #409eff;
}

.move-buttons .el-button:disabled {
  color: #c0c4cc;
  cursor: not-allowed;
}

/* 中间按钮 */
.transfer-buttons {
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  
}

.transfer-buttons .el-button+.el-button{
  padding: 0 15px;
  margin: 0;
}

/* 拖拽样式 */
.transfer-item.dragging {
  opacity: 0.5;
  background-color: #e6f7ff;
  border: 1px dashed #409eff;
}

.transfer-item[draggable="true"]:hover {
  background-color: #f5f7fa;
}

.transfer-item[draggable="true"] {
  cursor: grab;
}

.transfer-item[draggable="true"]:active {
  cursor: grabbing;
}

/* 空提示 */
.empty-tip {
  text-align: center;
  padding: 20px;
  color: #909399;
  font-size: 14px;
}

/* 表单标签样式 */
.form-label {
  font-weight: bold;
  margin-bottom: 8px;
  display: block;
  color: #333;
}

.mb-3 {
  margin-bottom: 16px;
}

/* 对话框底部 */
.dialog-footer {
  padding: 10px 20px 10px;
  text-align: center;
}
</style>